import { ScrollArea } from "@/components/ui/scroll-area";
import RecentMessages from "@/containers/messages/recent-messages";
import {
  ChatListResponseType,
  ChatListType,
  ChatPreviewType,
  UserType,
} from "@/common/types";
import { useEffect, useState } from "react";
import ChatPreview from "@/containers/messages/chat-preview";
import { Input } from "@/components/ui/input";
import { SendHorizontal, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearChatList,
  getChatHistory,
  getChatList,
  getMoreChatList,
  sendMessage,
} from "@/redux/actions/chatActions";
import { RootState } from "@/redux/reducers";

const Messages = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [chatsList, setChatsList] = useState<ChatListType[]>([]);
  const [selectedChat, setSelectedChat] = useState<number | null>(null);
  const [selectedChatPreview, setSelectedChatPreview] = useState<
    ChatPreviewType[]
  >([]);
  const [nextPagePresent, setNextPagePresent] = useState<boolean>(false);
  const [formState, setFormState] = useState({
    message: "",
  });
  const [error, setError] = useState<string>("");

  const [showChatDetail, setShowChatDetail] = useState<boolean>(false);
  const [selectedChatUser, setSelectedChatUser] = useState<UserType | null>(
    null
  );

  const { chats, chatHistory, chatList } = useSelector(
    (state: RootState) => state.chat
  );

  const dispatch: any = useDispatch();

  const handleChange = (key: string, value: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const validateInput = () => {
    let errorMessage = "";
    if (!formState.message || formState.message === "") {
      errorMessage = "Enter a message";
    } else {
      errorMessage = "";
    }
    setError(errorMessage);
    return errorMessage;
  };

  const handleSendMessage = () => {
    const errorMessage = validateInput();
    if (!errorMessage) {
      const payload = {
        message: formState.message,
      };
      dispatch(
        sendMessage(selectedChat as number, payload, () =>
          setFormState({ message: "" })
        )
      );
    }
  };

  const handleRowClick = (message: ChatListType) => {
    setSelectedChat(message.other_user.id as number);
    setSelectedChatUser(message.other_user);
    setShowChatDetail(true);
  };

  const handleNewMessage = (user: UserType) => {
    const chatExist = chatList.find((chat) => chat.other_user.id === user.id);
    if (!chatExist) {
      setChatsList([
        {
          other_user: user,
          last_message: {
            message: "",
            timestamp: new Date().toISOString(),
            send_by_me: false,
          },
          is_read: true,
        },
        ...chatList,
      ]);
    }
    setSelectedChat(user.id as number);
  };

  const loadNextPage = async () => {
    setCurrentPage(currentPage + 1);
    return (chats as ChatListResponseType).next;
  };

  useEffect(() => {
    setChatsList(chatList);
  }, [chatList]);

  useEffect(() => {
    if (chats) {
      setNextPagePresent((chats as ChatListResponseType)?.next !== null);
    }
  }, [chats]);

  useEffect(() => {
    if (chatHistory) {
      setSelectedChatPreview((chatHistory as ChatPreviewType[]) || []);
    }
  }, [chatHistory]);

  useEffect(() => {
    if (selectedChat) {
      dispatch(getChatHistory(selectedChat as number));
    }
  }, [selectedChat]);

  useEffect(() => {
    if (currentPage !== 1) {
      dispatch(getMoreChatList(currentPage));
    }
  }, [currentPage]);

  useEffect(() => {
    dispatch(getChatList(currentPage));
  }, []);

  useEffect(() => {
    dispatch(clearChatList());
  }, []);

  return (
    <div className="flex flex-col mx-4 md:mx-8 mt-4 md:mr-0 border-solid border-gray-200">
      {showChatDetail && (
        <div className="md:hidden border-b pb-2 flex items-center justify-between">
          <p className="md:hidden font-medium">{selectedChatUser?.name}</p>
          <button
            className="md:hidden"
            onClick={() => setShowChatDetail(false)}
          >
            {showChatDetail && <X size={24} />}
          </button>
        </div>
      )}

      <div className="grid grid-cols-6 sm:grid-cols-6 md:grid-cols-3 lg:grid-cols-4 ">
        <ScrollArea
          className={`${
            showChatDetail ? "hidden md:block" : "block"
          } col-span-6 sm:col-span-6 md:col-span-1 lg:col-span-1 border-t h-[calc(100vh-92px)] overflow-auto border-l border-r border-solid border-gray-200`}
        >
          <RecentMessages
            recentMessages={chatsList}
            handleRowClick={handleRowClick}
            selectedChat={selectedChat}
            handleNewMessage={handleNewMessage}
            loadNextPage={loadNextPage}
            nextPagePresent={nextPagePresent}
          />
        </ScrollArea>

        {/* Chat Preview Web Devices */}
        <div className="col-span-6 sm:col-span-6 md:col-span-2 lg:col-span-3 hidden md:block border-t">
          {((selectedChatPreview && selectedChatPreview.length > 0) ||
            selectedChat) && <ChatPreview chatPreview={selectedChatPreview} />}
          {((selectedChatPreview && selectedChatPreview.length > 0) ||
            selectedChat) && (
            <>
              <p className="text-red-500 text-sm py-0 px-4 h-[20px]">{error}</p>
              <div className="flex items-center gap-2 py-3 px-4">
                <Input
                  id="message"
                  name="message"
                  value={formState.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                />
                <SendHorizontal
                  className="w-6 h-6"
                  onClick={() => handleSendMessage()}
                />
              </div>
            </>
          )}
          {!(selectedChatPreview.length > 0) && !selectedChat && (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm">No preview found</p>
            </div>
          )}
        </div>

        {/* Chat Preview Mobile Devices */}
        {showChatDetail && (
          <div className="block md:hidden col-span-6">
            {((selectedChatPreview && selectedChatPreview.length > 0) ||
              selectedChat) && (
              <ChatPreview
                chatPreview={selectedChatPreview}
                heightToReduce="200px"
              />
            )}
            {((selectedChatPreview && selectedChatPreview.length > 0) ||
              selectedChat) && (
              <>
                <p className="text-red-500 text-sm py-0 px-4 h-[20px]">
                  {error}
                </p>
                <div className="flex items-center gap-2 py-3">
                  <Input
                    id="message"
                    name="message"
                    value={formState.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                  />
                  <SendHorizontal
                    className="w-6 h-6"
                    onClick={() => handleSendMessage()}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
